import { useState } from 'react';
import { X, Eye, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DesignPointResponse,
  StudyDetailResponse,
} from '@/api/generated/types.gen';
import {
  handleUpdateStudyStudiesStudyIdPatchMutation,
  handleGetStudyStudiesStudyIdGetQueryKey,
  handleListStudiesStudiesGetQueryKey,
} from '@/api/generated/@tanstack/react-query.gen';
import { useStore } from '@/stores/useStore';
import { useGeometryMeshStatuses } from '@/hooks/useGeometryMeshStatuses';
import { allMeshed, useDraftGeometryIds } from './meshing/useDraftGeometryIds';
import { SimpleTooltip } from '@/components/SimpleTooltip';
import { ReadOnlyBanner } from '@/components/share/ReadOnlyBanner';
import { isBelowEditorRole } from '@/utils/shareAccess';
import StudyHeaderBar from './StudyHeaderBar';
import { AnalyzePhaseBreadcrumb } from './AnalyzePhaseBreadcrumb';
import { JourneyStrip } from './meshing/JourneyStrip';
import type { StudyLibrary } from '../-data/constants';
import { useDraftFormStatus } from './draft/useEditingManualDp';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  STEP_SLUGS,
  isResultsPath,
  statusToJourneyStep,
  stepSlugFromPath,
} from './meshing/status';
import { cn } from '@/lib/utils';

const NO_IDS: string[] = [];

interface StudyHeaderProps {
  study: StudyDetailResponse;
  designPoints: DesignPointResponse[];
  library: StudyLibrary;
}

export default function StudyHeader({
  study,
  designPoints,
  library,
}: StudyHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const draftStudyId = useStore((s) => s.draftStudyId);
  const selectedInputs = useStore((s) => s.draftConfig.selected_inputs);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routedSlug = stepSlugFromPath(pathname);
  const routedStep = routedSlug ? STEP_SLUGS.indexOf(routedSlug) : -1;
  // A geometry-FE-meshed draft persists its per-geometry mesh picks in
  // `inputs.mesh_ids` ALONGSIDE `geometry_ids` — those are not "meshes picked
  // upfront", so the Mesh step stays active. Only a true mesh-in study
  // (`mesh_ids` with no `geometry_ids`) skips meshing.
  const savedMeshInputCount = study.config?.inputs?.geometry_ids?.length
    ? 0
    : (study.config?.inputs?.mesh_ids?.length ?? 0);
  const activeDraftInputs = draftStudyId === study.id ? selectedInputs : null;
  const draftMeshInputCount =
    activeDraftInputs?.kind === 'mesh' ? activeDraftInputs.items.length : 0;
  const skipsMeshStep =
    study.status === 'draft' &&
    (draftMeshInputCount > 0 || savedMeshInputCount > 0);
  const currentStep =
    study.status === 'draft' && routedStep >= 0
      ? routedStep
      : statusToJourneyStep(study.status);
  // Drafts: Mesh is done once every draft geometry has a mesh (ENG-3100) —
  // it used to stay unchecked however much had been meshed. Nothing later is
  // done before the run.
  // Follows the page's own subscription rather than polling a second time.
  const draftIds = useDraftGeometryIds(study.id, study);
  const draftGeometryIds = study.status === 'draft' ? draftIds : NO_IDS;
  const { statusByGeomId: draftMeshStatus } = useGeometryMeshStatuses(
    draftGeometryIds,
    { poll: false },
  );
  const draftMeshDone = allMeshed(draftGeometryIds, draftMeshStatus);
  const doneIdx =
    study.status === 'draft' ? (draftMeshDone ? 1 : 0) : currentStep;
  const facesDoneCount = designPoints.filter(
    (dp) => dp.status === 'faces_done',
  ).length;
  const journeyBranches =
    study.status !== 'draft' && currentStep === 1
      ? t('simopsShared.studyHeader.dpsReady', {
          done: facesDoneCount,
          total: study.total_points,
        })
      : undefined;

  // ENG-1842 — the Results route's CONTROLS (the mode tabs, the density gear
  // and the Writeup + Export buttons) used to take over this header's tail
  // slot. They are a strip of their own now, rendered by the results route
  // ABOVE both the dashboard and the writeup drawer — see `AnalyzeControlBar`,
  // which records why.
  //
  // The phase breadcrumb did NOT go with them. It says where the STUDY is,
  // which is what the rest of this row says; the strip below is things the
  // reader operates. It also fills the slot the JourneyStrip holds on every
  // other step — this route only mounts for completed studies (see
  // results.tsx), so Mesh + Simulate are always finished and a step-clicker
  // is not the control the analysis surface wants.
  //
  // Shared with `report/reportExportAffordances.ts`, which decides whether the
  // export controls this header mounts are on the page — see `isResultsPath`.
  //
  // ENG-1917 — the bare `/simops/$studyId` URL (`index.tsx`) also mounts
  // `CompletedView` (which mounts `EvaluationWorkbench`) for a completed
  // study, so the tabs belong there too. Without this, a viewer forwarded
  // from `/share/study/:id` (or navigating directly to the bare URL) sees
  // the workbench body but no Overview | Points picker and cannot leave
  // Overview. The path check keys off the STUDY status here rather than
  // widening `isResultsPath`, so the export-affordance table's own reading of
  // the step stays untouched.
  const isBareStudyUrl = routedSlug === undefined;
  const showsResultsSurface =
    (study.status === 'completed' ||
      study.status === 'completed_with_issues') &&
    isBareStudyUrl;
  const isResultsRoute = isResultsPath(pathname) || showsResultsSurface;

  return (
    // Title + DOE/Draft/Private badges + last-saved on the left, inline
    // stepper on the right. The bar grows a second row when the groups do
    // not fit — see StudyHeaderBar — instead of stacking the title one
    // word per line.
    <div className="shrink-0 border-b border-border bg-card px-6 py-3.5">
      <StudyHeaderBar
        study={study}
        library={library}
        title={<EditableStudyName studyId={study.id} name={study.name} />}
        trailing={
          study.status === 'draft' ? (
            <span className="flex items-center gap-3">
              <DraftFormStatusChip />
              <SaveIndicator />
            </span>
          ) : isBelowEditorRole(study.my_role) ? (
            <ReadOnlyBanner
              role={study.my_role}
              ownerEmail={null}
              placement="header"
            />
          ) : undefined
        }
        // The phase breadcrumb takes the STEPPER slot, not the trailing one:
        // it is the same `Mesh › Simulate › Results` reading the JourneyStrip
        // gives on every other step, so it belongs at the same end of the row.
        // In `trailing` it sat against the status pill and read as another
        // badge on the study's name.
        stepper={
          isResultsRoute ? (
            <AnalyzePhaseBreadcrumb
              simulationsRunning={study.status === 'running'}
              onSimulateClick={() => {
                void navigate({
                  to: '/simops/$studyId/simulate',
                  params: { studyId: study.id },
                });
              }}
            />
          ) : (
            <JourneyStrip
              inline
              current={currentStep}
              doneIdx={doneIdx}
              branches={journeyBranches}
              skippedStepIds={skipsMeshStep ? ['mesh'] : undefined}
              onStepClick={(index) => {
                const step = STEP_SLUGS[index];
                if (!step) return;
                // Mesh step is skipped when mesh inputs were picked upfront —
                // forward the click to Simulate so the JourneyStrip stays
                // clickable without landing on an off-phase placeholder.
                const target =
                  step === 'mesh' && skipsMeshStep ? 'simulate' : step;
                // `/simops` even for an EvalTool reader, and NOT a
                // `STUDY_DETAIL_ROUTES[library]` interpolation: the step routes
                // exist under SimOps only, so a per-library prefix here would
                // build `/evaltool/$studyId/mesh`, which is not a route — and
                // an interpolated `to` is just a string to the router's type
                // checker, so nothing would say so. Unreachable from EvalTool
                // today (the strip is swapped for the phase breadcrumb on
                // results paths), but a step click means "go to that step",
                // which is a SimOps address.
                void navigate({
                  to: `/simops/$studyId/${target}`,
                  params: { studyId: study.id },
                });
              }}
            />
          )
        }
      />
    </div>
  );
}

interface EditableStudyNameProps {
  studyId: string;
  name: string;
}

function EditableStudyName({ studyId, name }: EditableStudyNameProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const renameMutation = useMutation({
    ...handleUpdateStudyStudiesStudyIdPatchMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: handleGetStudyStudiesStudyIdGetQueryKey({
          path: { study_id: studyId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: handleListStudiesStudiesGetQueryKey(),
      });
    },
  });

  const startEdit = () => {
    setDraft(name);
    setIsEditing(true);
  };

  const commit = () => {
    const next = draft.trim();
    setIsEditing(false);
    if (!next || next === name) return;
    renameMutation.mutate({
      path: { study_id: studyId },
      body: { name: next },
    });
  };

  if (isEditing) {
    return (
      <input
        aria-label={t('simopsShared.studyHeader.studyNameLabel')}
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setIsEditing(false);
        }}
        onBlur={commit}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full min-w-0 rounded border border-border bg-card px-2 py-0.5 text-lg font-semibold text-foreground focus:border-info focus:outline-hidden"
      />
    );
  }

  return (
    <>
      <SimpleTooltip content={t('simopsShared.studyHeader.renameTooltip')}>
        <h1
          role="button"
          tabIndex={0}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEdit();
            }
          }}
          className="block max-w-full cursor-pointer truncate text-lg font-semibold text-foreground hover:text-info focus-visible:outline-2 focus-visible:outline-ring"
        >
          {name}
        </h1>
      </SimpleTooltip>
      {renameMutation.isError && (
        <span className="text-xs text-destructive">
          {t('simopsShared.studyHeader.renameFailed')}
        </span>
      )}
    </>
  );
}

/**
 * Compact status chip shown next to SaveIndicator during draft authoring.
 * Two states:
 * - `viewing` (default): grey Eye icon + "Viewing study defaults" — signals
 *   that field edits go to the study-wide defaults (they affect every DP
 *   that doesn't override that axis).
 * - `editing`: colored Pencil icon + DP color dot + "Editing DP: <label>"
 *   with an inline ✕ to exit. Signals that field edits SHOULD land on the
 *   focused manual DP — but sweep-value inputs still affect the sweep list,
 *   not the DP (matches the solver-form hint copy).
 * Both mirror the sidebar banner so the focused-DP state stays visible
 * even when the user scrolls the sidebar or drops the viewer full-screen.
 */
function DraftFormStatusChip() {
  const { t } = useTranslation();
  const status = useDraftFormStatus(t);
  const setEditingManualDpId = useStore((s) => s.setEditingManualDpId);
  const clearSelectedManualDpIds = useStore((s) => s.clearSelectedManualDpIds);
  const isEditing = status.mode === 'editing';
  const isMulti = status.mode === 'multi';
  const Icon = isEditing ? Pencil : isMulti ? Pencil : Eye;
  const dismiss = isEditing
    ? () => setEditingManualDpId(null)
    : isMulti
      ? () => clearSelectedManualDpIds()
      : undefined;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={dismiss}
            title={
              isEditing || isMulti
                ? t('simopsDraft.studyPreview.doneEditing')
                : undefined
            }
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-2xs text-foreground',
              isEditing
                ? 'cursor-pointer border-primary/40 bg-primary/5 hover:bg-primary/10'
                : isMulti
                  ? 'simops-multi-orbit cursor-pointer border-destructive/40 bg-destructive/10 text-destructive-strong hover:bg-destructive/15'
                  : 'cursor-default border-border bg-muted/40',
            )}
          >
            <Icon
              className="h-3 w-3"
              style={{
                color: isEditing || isMulti ? status.stripe : undefined,
              }}
            />
            {(isEditing || isMulti) && (
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: status.stripe }}
              />
            )}
            <span>
              {isEditing
                ? t('simopsDraft.studyPreview.editingBanner', {
                    label: status.label,
                  })
                : status.label}
            </span>
            {(isEditing || isMulti) && (
              <X className="h-3 w-3 text-muted-foreground" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-72 text-xs">
          {isEditing
            ? t('simopsDraft.studyPreview.editingChipTooltip')
            : isMulti
              ? t('simopsDraft.studyPreview.multiSelectChipTooltip', {
                  count: status.multiCount,
                })
              : t('simopsDraft.studyPreview.viewingChipTooltip')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SaveIndicator() {
  const { t } = useTranslation();
  const saveStatus = useStore((s) => s.saveStatus);
  const lastSavedAt = useStore((s) => s.lastSavedAt);

  const text =
    saveStatus === 'saving'
      ? t('simopsShared.studyHeader.saving')
      : saveStatus === 'error'
        ? t('simopsShared.studyHeader.saveFailed')
        : lastSavedAt
          ? t('simopsShared.studyHeader.lastSaved', {
              time: lastSavedAt.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })
          : '';

  if (!text) return null;

  return <span className="text-2xs text-muted-foreground">{text}</span>;
}
